import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, CheckCircle2, UploadCloud, LayoutList } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errMsg, API } from "@/lib/api";
import { portalApi } from "@/lib/portal";
import { PortalPostCard } from "@/components/portal/PortalPostCard";
import { PortalUpload } from "@/components/portal/PortalUpload";

const Shell = ({ children, cliente, logoKey }) => (
  <div className="core-public min-h-screen bg-[#7CC6FA]" data-testid="portal-page">
    <header className="border-b border-[#5FB3EA] bg-[#8FD0FB]">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-4 sm:py-6 flex flex-wrap items-center justify-between gap-3 sm:gap-6">
        <div className="flex items-center gap-4">
          <img src="/brand/logo-core.png" alt="Core" className="h-12 w-auto" data-testid="portal-logo" />
          {logoKey && <><span className="h-10 w-px bg-[#0A1128]/20" /><div className="h-14 rounded-xl bg-white px-3 py-1.5 flex items-center"><img src={`${API}/loghi/${logoKey}`} alt={cliente} className="max-h-11 max-w-[160px] object-contain" data-testid="portal-client-logo" /></div></>}
        </div>
        {cliente && <div className="text-right"><div className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8173A]">Portale cliente</div><div className="font-display text-xl font-black text-[#0A1128]" data-testid="portal-client-name">{cliente}</div></div>}
      </div>
    </header>
    <main className="core-main max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-10">{children}</main>
  </div>
);

export default function PortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setData((await portalApi.get(`/${token}`)).data); }
    catch (e) { setError(errMsg(e, "Link non valido o disattivato.")); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (error) return (
    <Shell><div className="max-w-lg mx-auto text-center rounded-2xl bg-[#8FD0FB] border border-[#5FB3EA] p-6 sm:p-10" data-testid="portal-error">
      <Lock className="h-10 w-10 text-[#E8173A] mx-auto mb-4" /><h1 className="font-display text-2xl font-black text-[#0A1128] mb-2">Portale non disponibile</h1><p className="text-[#0A1128]/80">{error}</p>
    </div></Shell>
  );
  if (!data) return <Shell><div className="text-[#0A1128]" data-testid="portal-loading">Caricamento...</div></Shell>;
  const pending = data.posts.filter((p) => p.stato === "in_revisione");
  const approved = data.posts.filter((p) => p.stato === "approvato");
  const replace = (np) => setData((d) => ({ ...d, posts: d.posts.map((p) => (p.id === np.id ? np : p)).filter((p) => ["in_revisione", "approvato"].includes(p.stato)) }));

  return (
    <Shell cliente={data.cliente} logoKey={data.logo_key}>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-[#0A1128] mb-2">Ciao {data.cliente}</h1>
      <p className="text-[#0A1128]/80 mb-8">Qui puoi approvare i contenuti preparati da {data.agenzia} e inviarci i tuoi materiali.</p>
      <Tabs defaultValue="contenuti">
        <TabsList className="bg-[#8FD0FB] rounded-full p-1 h-auto mb-6">
          <TabsTrigger value="contenuti" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="portal-tab-contenuti"><LayoutList className="h-4 w-4 mr-1.5" />Contenuti da approvare ({pending.length})</TabsTrigger>
          <TabsTrigger value="materiali" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" data-testid="portal-tab-materiali"><UploadCloud className="h-4 w-4 mr-1.5" />Carica materiali</TabsTrigger>
        </TabsList>
        <TabsContent value="contenuti" className="space-y-5">
          {!pending.length && <div className="rounded-2xl bg-[#8FD0FB] border border-[#5FB3EA] p-8 text-center text-[#0A1128]" data-testid="portal-no-pending"><CheckCircle2 className="h-8 w-8 text-emerald-700 mx-auto mb-2" />Nessun contenuto in attesa di approvazione. Grazie!</div>}
          {pending.map((p) => <PortalPostCard key={p.id} post={p} token={token} onChange={replace} />)}
          {approved.length > 0 && <h2 className="text-lg font-black text-[#0A1128] pt-6">Già approvati</h2>}
          {approved.map((p) => <PortalPostCard key={p.id} post={p} token={token} readOnly />)}
          {!!data.ped_files?.length && (
            <section className="pt-6" data-testid="portal-ped-files">
              <h2 className="text-lg font-black text-[#0A1128] mb-3">Documenti del piano editoriale</h2>
              <div className="grid gap-2">{data.ped_files.map((f) => (
                <a key={f.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/portale/${token}/ped-file/${f.id}`} className="flex items-center justify-between gap-3 rounded-2xl bg-white/80 border border-[#5FB3EA] px-4 py-3 min-h-[52px] font-bold text-[#0A1128] hover:border-[#E8173A]" data-testid={`portal-ped-file-${f.id}`}>
                  <span className="truncate">{f.nome}</span><span className="text-xs font-semibold text-slate-500 shrink-0">{f.mese ? new Date(`${f.mese}-01T12:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : "Scarica"}</span>
                </a>))}</div>
            </section>
          )}
        </TabsContent>
        <TabsContent value="materiali"><PortalUpload token={token} /></TabsContent>
      </Tabs>
    </Shell>
  );
}
